import React, { useState } from "react";
import { Form, Input, Button, message, Card, Spin, Row, Col, Tooltip, Typography, Progress, Checkbox } from "antd";
import { MailOutlined, PhoneOutlined, LockOutlined, UserOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

const { Title, Text } = Typography;

const CompanyRegistration = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [sameAsOffice, setSameAsOffice] = useState(false);

  // Password strength (0–100)
  const evaluatePasswordStrength = (password) => {
    let score = 0;
    if (!password) return 0;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*]/.test(password)) score++;
    return score * 25;
  };

  // Sync delivery fields when office fields or the checkbox change
  const handleValuesChange = (changed, all) => {
    if ("sameAsOffice" in changed) {
      setSameAsOffice(changed.sameAsOffice);
      if (!changed.sameAsOffice) {
        form.setFieldsValue({
          delivery_address: "",
          delivery_city: "",
          delivery_eir_code: "",
        });
      }
    }
    if (all.sameAsOffice) {
      form.setFieldsValue({
        delivery_address: all.office_address,
        delivery_city: all.office_city,
        delivery_eir_code: all.office_eir_code,
      });
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const resp = await axiosInstance.post("/auth/user/register", {
        full_name: values.full_name,
        email: values.email,
        address: values.address,
        company_name: values.company_name,
        office_address: values.office_address,
        office_city: values.office_city,
        office_eir_code: values.office_eir_code,
        invoice_email: values.invoice_email,
        delivery_address: values.delivery_address,
        delivery_city: values.delivery_city,
        delivery_eir_code: values.delivery_eir_code,
        phone_number: values.phone_number,
        password: values.password,
      });

      if (resp.data.success) {
        message.success("User registered—pending admin approval!");
        form.resetFields();
        navigate("/requests", { state: { newCompany: resp.data.user } });
      } else {
        message.error(resp.data.message || "Registration failed.");
      }
    } catch (err) {
      console.error("Registration error:", err.response?.data || err.message);
      const msg = err.response?.data?.message || "Please try again.";
      if (msg.toLowerCase().includes("exist")) {
        form.setFields([
          {
            name: "email",
            errors: ["Email already exists, please use another"],
          },
        ]);
      } else {
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600 font-medium">Registering user...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 lg:p-6">
      <div className="max-w-2xl mx-auto">
        <Card className="border-0 shadow-sm rounded-2xl bg-white overflow-hidden">
          <div className="p-6 lg:p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-2xl mb-4">
                <UserOutlined className="text-2xl text-blue-600" />
              </div>
              <Title level={2} className="text-gray-900 mb-2">
                User Registration
              </Title>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={handleValuesChange}
              initialValues={{ sameAsOffice: false }}
              className="space-y-6"
            >
              {/* Personal Info */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Personal Info
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="full_name"
                      label="Full Name"
                      rules={[
                        { required: true, message: "Enter full name" },
                        { pattern: /^[A-Za-z\s]+$/, message: "Letters & spaces only" },
                      ]}
                    >
                      <Input 
                        prefix={<UserOutlined className="text-gray-400" />} 
                        placeholder="Full name" 
                        autoFocus 
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: "Enter email" },
                        { type: "email", message: "Valid email required" },
                      ]}
                    >
                      <Input 
                        prefix={<MailOutlined className="text-gray-400" />} 
                        placeholder="Email" 
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Phone Number with flag + code */}
                <Row gutter={[16, 8]}>
                  <Col xs={24}>
                    <Form.Item
                      name="phone_number"
                      label="Phone Number"
                      rules={[
                        { required: true, message: "Enter phone number" },
                        {
                          validator: (_, value) =>
                            value && value.replace(/\D/g, "").length >= 7
                              ? Promise.resolve()
                              : Promise.reject(new Error("Valid phone required")),
                        },
                      ]}
                    >
                      <PhoneInput
                        country={"ie"}
                        value={form.getFieldValue("phone_number")}
                        onChange={(phone) => form.setFieldsValue({ phone_number: phone })}
                        enableSearch
                        inputStyle={{ 
                          width: "100%", 
                          height: "44px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          fontSize: "14px"
                        }}
                        buttonStyle={{ 
                          borderRadius: "8px 0 0 8px",
                          background: "#f9fafb",
                          border: "1px solid #d1d5db",
                          borderRight: "none"
                        }}
                        dropdownStyle={{ 
                          zIndex: 1000,
                          borderRadius: "8px"
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Company Info */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Company Info
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="company_name"
                      label="Company Name"
                      rules={[{ required: true, message: "Enter company name" }]}
                    >
                      <Input 
                        placeholder="Company name" 
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="address"
                      label="Address"
                      rules={[{ required: true, message: "Enter address" }]}
                    >
                      <Input 
                        placeholder="Address" 
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Office Details */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  Office Details
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24}>
                    <Form.Item
                      name="office_address"
                      label="Office Address"
                      rules={[{ required: true, message: "Enter office address" }]}
                    >
                      <Input 
                        placeholder="Office address" 
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="office_city"
                      label="City"
                      rules={[{ required: true, message: "Enter office city" }]}
                    >
                      <Input 
                        placeholder="City" 
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="office_eir_code"
                      label="EIR/ Postal Code"
                      rules={[{ required: true, message: "Enter office EIR/Post code" }]}
                    >
                      <Input 
                        placeholder="EIR / Postal code" 
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Same as Office Checkbox */}
                <Row gutter={[16, 8]}>
                  <Col xs={24}>
                    <Form.Item name="sameAsOffice" valuePropName="checked" className="mb-0">
                      <Checkbox className="flex items-center">
                        <span className="text-gray-700">Same as Office Address</span>
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Delivery Details */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  Delivery Details
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24}>
                    <Form.Item
                      name="delivery_address"
                      label="Delivery Address"
                      rules={[{ required: true, message: "Enter delivery address" }]}
                    >
                      <Input 
                        placeholder="Delivery address" 
                        disabled={sameAsOffice}
                        className={`h-11 rounded-lg border-gray-300 ${sameAsOffice ? 'bg-gray-100 text-gray-500' : 'hover:border-blue-500 focus:border-blue-500'}`}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="delivery_city"
                      label="City"
                      rules={[{ required: true, message: "Enter delivery city" }]}
                    >
                      <Input 
                        placeholder="City" 
                        disabled={sameAsOffice}
                        className={`h-11 rounded-lg border-gray-300 ${sameAsOffice ? 'bg-gray-100 text-gray-500' : 'hover:border-blue-500 focus:border-blue-500'}`}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="delivery_eir_code"
                      label="EIR/ Postal Code"
                      rules={[{ required: true, message: "Enter delivery EIR/Post code" }]}
                    >
                      <Input 
                        placeholder="EIR / Postal code" 
                        disabled={sameAsOffice}
                        className={`h-11 rounded-lg border-gray-300 ${sameAsOffice ? 'bg-gray-100 text-gray-500' : 'hover:border-blue-500 focus:border-blue-500'}`}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Invoice Details */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  Invoice Details
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24}>
                    <Form.Item
                      name="invoice_email"
                      label="Invoice Email"
                      rules={[
                        { required: true, message: "Enter invoice email" },
                        { type: "email", message: "Valid email required" },
                      ]}
                    >
                      <Input 
                        prefix={<MailOutlined className="text-gray-400" />} 
                        placeholder="Invoice email" 
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Security */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  Security
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="password"
                      label="Password"
                      hasFeedback
                      rules={[
                        { required: true, message: "At least 8 characters required", min: 8 },
                        {
                          pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/,
                          message: "Must include uppercase, lowercase, number & special char",
                        },
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined className="text-gray-400" />}
                        placeholder="Create a password"
                        onChange={(e) => {
                          setPasswordStrength(evaluatePasswordStrength(e.target.value));
                        }}
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                    <Tooltip title="Use at least 8 chars with uppercase, lowercase, number & special char">
                      <Text className="text-gray-500 text-xs block mt-1">
                        Password must include uppercase, lowercase, number & special char.
                      </Text>
                    </Tooltip>
                    {form.getFieldValue("password") && (
                      <div className="mt-2 space-y-1">
                        <Progress
                          percent={passwordStrength}
                          showInfo={false}
                          strokeColor={
                            passwordStrength < 50
                              ? "#ef4444"
                              : passwordStrength < 100
                              ? "#f59e0b"
                              : "#10b981"
                          }
                          className="mb-1"
                        />
                        <Text className="text-xs">
                          {passwordStrength < 50
                            ? "Weak"
                            : passwordStrength < 100
                            ? "Medium"
                            : "Strong"}
                        </Text>
                      </div>
                    )}
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="confirm_password"
                      label="Confirm Password"
                      dependencies={["password"]}
                      hasFeedback
                      rules={[
                        { required: true, message: "Confirm your password" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            return value && getFieldValue("password") === value
                              ? Promise.resolve()
                              : Promise.reject(new Error("Passwords do not match"));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined className="text-gray-400" />}
                        placeholder="Confirm your password"
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <Form.Item className="mb-0">
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 border-0 rounded-xl text-lg font-semibold shadow-sm transition-colors"
                >
                  Register User
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CompanyRegistration;